import {
	Button,
	Calendar,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import { endOfDay, format, startOfDay, subDays, subMonths } from "date-fns";
import {
	ArrowLeft,
	CalendarIcon,
	Check,
	Copy,
	Store,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import { BLOCKCHAIN_NAMES } from "src/constants/blockchains";
import type {
	MerchantDetail,
	MerchantSeriesPoint,
	MerchantVolumeSlice,
	SeriesGranularity,
} from "src/providers/merchant-detail-provider";
import { useMerchantDetail } from "src/queries/merchant-detail-queries";
import { formatCryptoAmount, splitTicker } from "src/utils/format";

interface DateRange {
	from: Date;
	to: Date;
}

const PRESETS: Array<{ label: string; days: number }> = [
	{ label: "7 days", days: 7 },
	{ label: "30 days", days: 30 },
	{ label: "90 days", days: 90 },
	{ label: "1 year", days: 365 },
];

const presetRange = (days: number): DateRange => ({
	from: startOfDay(subDays(new Date(), days - 1)),
	to: endOfDay(new Date()),
});

// Earliest selectable date in the range picker (platform predates no orders).
const MIN_PICKER_DATE = new Date("2020-01-01");

const usd = (n: number) =>
	new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(n);

const num = (n: number) => new Intl.NumberFormat("en-US").format(n);

const compactUsd = (n: number) =>
	n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${Math.round(n)}`;

const pointLabel = (dateStr: string, granularity: SeriesGranularity) => {
	const d = new Date(`${dateStr}T00:00:00Z`);
	if (Number.isNaN(d.getTime())) return dateStr;
	return granularity === "month"
		? d.toLocaleDateString("en-US", {
				month: "short",
				year: "2-digit",
				timeZone: "UTC",
			})
		: d.toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				timeZone: "UTC",
			});
};

const ymd = (y: number, m: number, d: number) =>
	`${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

// Every bucket key (yyyy-MM-dd) across [from, to] at the given granularity,
// matching the server's date_trunc keys so we can zero-fill missing buckets.
const bucketKeys = (
	from: Date,
	to: Date,
	granularity: SeriesGranularity,
): string[] => {
	const keys: string[] = [];
	if (granularity === "month") {
		let y = from.getFullYear();
		let m = from.getMonth();
		const ey = to.getFullYear();
		const em = to.getMonth();
		while (y < ey || (y === ey && m <= em)) {
			keys.push(ymd(y, m, 1));
			m += 1;
			if (m > 11) {
				m = 0;
				y += 1;
			}
		}
	} else {
		const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
		const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
		while (cur <= end) {
			keys.push(ymd(cur.getFullYear(), cur.getMonth(), cur.getDate()));
			cur.setDate(cur.getDate() + 1);
		}
	}
	return keys;
};

const densify = (
	series: MerchantSeriesPoint[],
	from: Date,
	to: Date,
	granularity: SeriesGranularity,
): MerchantSeriesPoint[] => {
	const byDate = new Map(series.map((p) => [p.date, p]));
	// Union the full range with any actual keys so no data point is ever dropped,
	// then sort (yyyy-MM-dd sorts chronologically).
	const keys = new Set(bucketKeys(from, to, granularity));
	for (const p of series) keys.add(p.date);
	return [...keys]
		.sort()
		.map(
			(date) =>
				byDate.get(date) ?? { date, orderCount: 0, paidCount: 0, volumeUsd: 0 },
		);
};

const MerchantDetailPage: React.FC = () => {
	const { merchantId } = useParams<{ merchantId: string }>();
	const [range, setRange] = React.useState<DateRange>(() => presetRange(365));
	const startDate = format(range.from, "yyyy-MM-dd");
	const endDate = format(range.to, "yyyy-MM-dd");
	const { data, isLoading, isError, isFetching, refetch } = useMerchantDetail(
		merchantId,
		startDate,
		endDate,
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<Link
				to="/merchants"
				className="inline-flex items-center gap-1.5 py-1 text-sm text-muted-foreground hover:text-foreground"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to merchants
			</Link>

			{isLoading ? (
				<DetailSkeleton />
			) : isError || !data ? (
				<Card>
					<CardContent className="py-12 text-center space-y-3">
						<p className="text-sm text-muted-foreground">
							Couldn't load this merchant.
						</p>
						<Button variant="outline" size="sm" onClick={() => refetch()}>
							Retry
						</Button>
					</CardContent>
				</Card>
			) : (
				<MerchantDetailBody
					data={data}
					range={range}
					onRangeChange={setRange}
					isFetching={isFetching}
				/>
			)}
		</div>
	);
};

interface MerchantDetailBodyProps {
	data: MerchantDetail;
	range: DateRange;
	onRangeChange: (range: DateRange) => void;
	isFetching: boolean;
}

const MerchantDetailBody: React.FC<MerchantDetailBodyProps> = ({
	data,
	range,
	onRangeChange,
	isFetching,
}) => {
	const navigate = useNavigate();
	const { merchant, headline, balances, orderSeries } = data;
	const [copied, setCopied] = React.useState(false);

	const copyUuid = () => {
		navigator.clipboard
			.writeText(merchant.uuid)
			.then(() => {
				setCopied(true);
				toast.success("Merchant ID copied");
				setTimeout(() => setCopied(false), 1500);
			})
			.catch(() => toast.error("Couldn't copy to clipboard"));
	};

	const ordersHref = `/orders?merchantId=${merchant.id}`;
	const netPositive = headline.netContributionUsd >= 0;
	const netClass = netPositive
		? "text-[var(--trend-positive)]"
		: "text-[var(--trend-negative)]";

	const periodVolume = orderSeries.reduce((sum, p) => sum + p.volumeUsd, 0);
	const periodOrders = orderSeries.reduce((sum, p) => sum + p.orderCount, 0);
	const periodPaid = orderSeries.reduce((sum, p) => sum + p.paidCount, 0);
	const hasActivity = orderSeries.length > 0;
	// The series only carries buckets that had orders; fill the gaps with zeros
	// so the chart spans the whole selected range, not just active days.
	const denseSeries = React.useMemo(
		() => densify(orderSeries, range.from, range.to, data.granularity),
		[orderSeries, range.from, range.to, data.granularity],
	);

	return (
		<>
			{/* Header */}
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="space-y-1">
					<div className="flex items-center gap-3">
						<Store className="h-6 w-6 text-muted-foreground" />
						<h1 className="text-3xl font-bold">{merchant.name}</h1>
						{merchant.status === "deleted" ? (
							<StatusBadge type="error">Deleted</StatusBadge>
						) : (
							<StatusBadge type="success">Active</StatusBadge>
						)}
					</div>
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
						{merchant.creatorEmail && <span>{merchant.creatorEmail}</span>}
						<span>
							Joined <TimeLabel time={merchant.createdAt} />
						</span>
						{merchant.website && (
							<a
								href={merchant.website}
								target="_blank"
								rel="noopener noreferrer"
								className="underline underline-offset-2"
							>
								{merchant.website}
							</a>
						)}
						<button
							type="button"
							onClick={copyUuid}
							title={merchant.uuid}
							aria-label="Copy merchant ID"
							className="inline-flex items-center gap-1 py-1 font-mono text-xs hover:text-foreground"
						>
							{merchant.uuid.slice(0, 8)}…
							{copied ? (
								<Check className="h-3 w-3 text-[var(--trend-positive)]" />
							) : (
								<Copy className="h-3 w-3" />
							)}
						</button>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<PeriodRangePicker value={range} onChange={onRangeChange} />
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigate(ordersHref)}
					>
						View orders
					</Button>
				</div>
			</div>

			{/* Lead panel: the all-time position this page answers */}
			<div className="rounded-xl border bg-card p-6 sm:p-8">
				<div className="grid sm:grid-cols-2 gap-8">
					<div>
						<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
							Net contribution
						</div>
						<div
							className={`mt-2 flex items-center gap-2 text-4xl sm:text-5xl font-bold tracking-tight tabular-nums ${netClass}`}
						>
							{netPositive ? (
								<TrendingUp className="h-7 w-7" />
							) : (
								<TrendingDown className="h-7 w-7" />
							)}
							{usd(headline.netContributionUsd)}
						</div>
						<p className="mt-2 text-xs text-muted-foreground">
							Platform fees earned ({usd(headline.platformRevenueUsd)}) minus
							energy spent serving them ({usd(headline.energyCostUsd)}),
							all-time.
						</p>
					</div>
					<div className="sm:border-l sm:border-border sm:pl-8">
						<div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
							Current balance
						</div>
						<div className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight tabular-nums">
							{usd(headline.totalBalanceUsd)}
						</div>
						<p className="mt-2 text-xs text-muted-foreground">
							Held across wallets right now, owed back to the merchant.
						</p>
					</div>
				</div>

				<div className="mt-6 pt-6 border-t grid grid-cols-2 sm:grid-cols-4 gap-6">
					<SubMetric
						label="Platform revenue"
						value={usd(headline.platformRevenueUsd)}
						hint={`Orders ${usd(headline.orderRevenueUsd)} · WD ${usd(
							headline.withdrawalRevenueUsd,
						)}`}
					/>
					<SubMetric
						label="Energy cost"
						value={usd(headline.energyCostUsd)}
						hint={`${num(headline.energyCostTrx)} TRX`}
					/>
					<SubMetric
						label="Lifetime volume"
						value={usd(headline.lifetimeVolumeUsd)}
						hint={`${num(headline.uniqueCustomers)} customers`}
					/>
					<SubMetric
						label="Orders"
						value={num(headline.totalOrders)}
						hint={`${num(headline.paidOrders)} paid`}
					/>
				</div>
			</div>

			{/* Balances */}
			<Card>
				<CardHeader className="pb-2">
					<CardTitle
						role="heading"
						aria-level={2}
						className="text-base font-semibold"
					>
						Balances
					</CardTitle>
					<CardDescription className="text-xs">
						What this merchant currently holds, by currency.
					</CardDescription>
				</CardHeader>
				<CardContent className="px-0 sm:px-2">
					{balances.length === 0 ? (
						<p className="px-6 py-6 text-sm text-muted-foreground">
							No balances.
						</p>
					) : (
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Currency</TableHead>
										<TableHead className="text-right">Amount</TableHead>
										<TableHead className="text-right">USD</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{balances.map((b) => {
										const { token, blockchain } = splitTicker(b.currency);
										return (
											<TableRow key={`${b.currency}-${b.blockchain}`}>
												<TableCell>
													<div className="flex items-center gap-2">
														<CryptoIcon
															ticker={b.currency}
															showComposite
															className="h-5 w-5"
														/>
														<div className="leading-tight">
															<div className="text-sm font-medium">
																{token || blockchain}
															</div>
															<div className="text-xs text-muted-foreground">
																{BLOCKCHAIN_NAMES[b.blockchain] || b.blockchain}
															</div>
														</div>
													</div>
												</TableCell>
												<TableCell className="text-right font-mono tabular-nums text-sm">
													{formatCryptoAmount(b.amount, {
														ticker: b.currency,
														maxDecimals: 8,
													})}
												</TableCell>
												<TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
													{b.usd === undefined ? null : usd(b.usd)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Volume over the selected period (the page's hero metric) */}
			<Card>
				<CardHeader className="pb-0 flex-row items-start justify-between gap-3">
					<div className="space-y-3">
						<CardTitle
							role="heading"
							aria-level={2}
							className="text-sm font-medium text-muted-foreground"
						>
							Volume
						</CardTitle>
						<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
							<span className="text-3xl sm:text-4xl font-bold tracking-tight tabular-nums">
								{usd(periodVolume)}
							</span>
							<span className="text-sm text-muted-foreground">
								from {num(periodPaid)} paid orders
							</span>
						</div>
					</div>
					<Link
						to={ordersHref}
						className="inline-block py-1 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline whitespace-nowrap"
					>
						View all orders
					</Link>
				</CardHeader>
				<CardContent
					className={`pt-4 pl-0 sm:pl-2 transition-opacity ${
						isFetching ? "opacity-50" : ""
					}`}
				>
					{hasActivity ? (
						<VolumeChart series={denseSeries} granularity={data.granularity} />
					) : (
						<EmptyChart range={range} />
					)}
				</CardContent>
			</Card>

			{/* Orders trend + asset breakdown, both over the selected period */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle
							role="heading"
							aria-level={2}
							className="text-base font-semibold"
						>
							Orders
						</CardTitle>
						<CardDescription className="text-xs">
							{num(periodOrders)} created · {num(periodPaid)} paid this period
						</CardDescription>
					</CardHeader>
					<CardContent
						className={`pl-0 sm:pl-2 transition-opacity ${
							isFetching ? "opacity-50" : ""
						}`}
					>
						{hasActivity ? (
							<>
								<OrdersChart
									series={denseSeries}
									granularity={data.granularity}
								/>
								<div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-3 text-xs text-muted-foreground">
									<LegendKey color="var(--chart-1)" label="Paid" />
									<LegendKey color="var(--chart-1)" label="Unpaid" faint />
								</div>
							</>
						) : (
							<EmptyChart range={range} />
						)}
					</CardContent>
				</Card>

				<BreakdownCard
					title="By currency"
					description="Share of paid volume, by asset"
					slices={data.currencyBreakdown}
				/>
			</div>

			{/* Recent activity (fixed windows) */}
			<Card>
				<CardHeader className="pb-2">
					<CardTitle
						role="heading"
						aria-level={2}
						className="text-base font-semibold"
					>
						Recent activity
					</CardTitle>
					<CardDescription className="text-xs">
						New orders created in each window.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5">
					<MiniStat
						label="Last 24h"
						value={num(data.recentActivity.ordersLast24h)}
					/>
					<MiniStat
						label="Last 7 days"
						value={num(data.recentActivity.ordersLast7d)}
					/>
					<MiniStat
						label="Last 30 days"
						value={num(data.recentActivity.ordersLast30d)}
					/>
					<MiniStat
						label="Active payment links"
						value={num(data.recentActivity.activePaymentLinks)}
					/>
				</CardContent>
			</Card>
		</>
	);
};

const PeriodRangePicker: React.FC<{
	value: DateRange;
	onChange: (range: DateRange) => void;
}> = ({ value, onChange }) => {
	const [open, setOpen] = React.useState(false);
	const [selection, setSelection] = React.useState<
		{ from: Date | undefined; to?: Date | undefined } | undefined
	>(undefined);
	const today = new Date();
	const isOutOfRange = (date: Date) => date > today || date < MIN_PICKER_DATE;

	const close = () => {
		setOpen(false);
		setSelection(undefined);
	};

	const applyPreset = (days: number) => {
		onChange(presetRange(days));
		close();
	};

	const applySelection = () => {
		if (selection?.from && selection?.to) {
			onChange({
				from: startOfDay(selection.from),
				to: endOfDay(selection.to),
			});
			close();
		}
	};

	const hasValidRange =
		selection?.from &&
		selection?.to &&
		selection.from.toDateString() !== selection.to.toDateString();

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setSelection(undefined);
			}}
		>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="justify-start text-left font-normal"
				>
					<CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
					<span className="truncate">
						{format(value.from, "MMM d")} – {format(value.to, "MMM d, yyyy")}
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="end">
				<div className="flex flex-col">
					<div className="flex flex-wrap gap-1 p-3 border-b">
						{PRESETS.map((preset) => (
							<Button
								key={preset.days}
								variant="ghost"
								size="sm"
								onClick={() => applyPreset(preset.days)}
							>
								{preset.label}
							</Button>
						))}
					</div>
					<Calendar
						mode="range"
						defaultMonth={value.to}
						selected={selection}
						onSelect={setSelection}
						numberOfMonths={1}
						disabled={isOutOfRange}
						className="sm:hidden"
					/>
					<Calendar
						mode="range"
						defaultMonth={subMonths(value.to, 1)}
						selected={selection}
						onSelect={setSelection}
						numberOfMonths={2}
						disabled={isOutOfRange}
						className="hidden sm:block"
					/>
					<div className="flex items-center justify-end gap-2 p-3 border-t">
						<Button variant="outline" size="sm" onClick={close}>
							Cancel
						</Button>
						<Button
							size="sm"
							onClick={applySelection}
							disabled={!hasValidRange}
						>
							Apply
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};

const VolumeChart: React.FC<{
	series: MerchantDetail["orderSeries"];
	granularity: SeriesGranularity;
}> = ({ series, granularity }) => {
	const chartData = series.map((p) => ({
		label: pointLabel(p.date, granularity),
		volume: p.volumeUsd,
	}));

	return (
		<ResponsiveContainer width="100%" height={300}>
			<AreaChart
				accessibilityLayer
				data={chartData}
				margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
			>
				<defs>
					<linearGradient id="merchantVolumeFill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.45} />
						<stop offset="55%" stopColor="var(--chart-1)" stopOpacity={0.12} />
						<stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
					</linearGradient>
				</defs>
				<CartesianGrid
					strokeDasharray="3 3"
					vertical={false}
					stroke="var(--chart-grid)"
				/>
				<XAxis
					dataKey="label"
					tickLine={false}
					axisLine={false}
					minTickGap={24}
					tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
				/>
				<YAxis
					tickLine={false}
					axisLine={false}
					width={48}
					tickFormatter={compactUsd}
					tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
				/>
				<Tooltip
					content={<VolumeTooltip />}
					cursor={{
						stroke: "var(--chart-axis)",
						strokeWidth: 1,
						strokeOpacity: 0.4,
					}}
				/>
				<Area
					type="monotone"
					dataKey="volume"
					name="Volume"
					stroke="var(--chart-1)"
					strokeWidth={2.5}
					fill="url(#merchantVolumeFill)"
					dot={false}
					activeDot={{ r: 3 }}
				/>
			</AreaChart>
		</ResponsiveContainer>
	);
};

const OrdersChart: React.FC<{
	series: MerchantDetail["orderSeries"];
	granularity: SeriesGranularity;
}> = ({ series, granularity }) => {
	const chartData = series.map((p) => ({
		label: pointLabel(p.date, granularity),
		paid: p.paidCount,
		unpaid: Math.max(0, p.orderCount - p.paidCount),
		total: p.orderCount,
	}));

	return (
		<ResponsiveContainer width="100%" height={220}>
			<BarChart
				accessibilityLayer
				data={chartData}
				margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
			>
				<CartesianGrid
					strokeDasharray="3 3"
					vertical={false}
					stroke="var(--chart-grid)"
				/>
				<XAxis
					dataKey="label"
					tickLine={false}
					axisLine={false}
					minTickGap={24}
					tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
				/>
				<YAxis
					allowDecimals={false}
					tickLine={false}
					axisLine={false}
					width={32}
					tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
				/>
				<Tooltip
					content={<OrdersTooltip />}
					cursor={{ fill: "var(--muted-foreground)", opacity: 0.08 }}
				/>
				<Bar
					dataKey="paid"
					name="Paid"
					stackId="o"
					fill="var(--chart-1)"
					maxBarSize={36}
				/>
				<Bar
					dataKey="unpaid"
					name="Unpaid"
					stackId="o"
					fill="var(--chart-1)"
					fillOpacity={0.4}
					radius={[3, 3, 0, 0]}
					maxBarSize={36}
				/>
			</BarChart>
		</ResponsiveContainer>
	);
};

const LegendKey: React.FC<{
	color: string;
	label: string;
	faint?: boolean;
}> = ({ color, label, faint }) => (
	<span className="flex items-center gap-1.5">
		<span
			className="h-2.5 w-2.5 rounded-[2px]"
			style={{ backgroundColor: color, opacity: faint ? 0.4 : 1 }}
		/>
		{label}
	</span>
);

const EmptyChart: React.FC<{ range: DateRange }> = ({ range }) => (
	<p className="py-16 text-sm text-muted-foreground text-center">
		No orders between {format(range.from, "MMM d, yyyy")} and{" "}
		{format(range.to, "MMM d, yyyy")}.
	</p>
);

const tooltipRow = (
	label: string,
	value: React.ReactNode,
	color: string,
	opacity = 1,
) => (
	<p className="flex items-center gap-2 text-muted-foreground">
		<span
			className="h-2 w-2 rounded-[2px]"
			style={{ backgroundColor: color, opacity }}
		/>
		{label}: <span className="ml-auto text-foreground">{value}</span>
	</p>
);

interface VolumeTooltipProps {
	active?: boolean;
	payload?: Array<{ payload: { label: string; volume: number } }>;
}

const VolumeTooltip: React.FC<VolumeTooltipProps> = ({ active, payload }) => {
	if (!active || !payload || payload.length === 0) return null;
	const p = payload[0].payload;
	return (
		<div className="min-w-[10rem] rounded-lg border bg-popover p-3 text-popover-foreground shadow-md text-xs space-y-1">
			<p className="font-medium">{p.label}</p>
			{tooltipRow("Volume", usd(p.volume), "var(--chart-1)")}
		</div>
	);
};

interface OrdersTooltipProps {
	active?: boolean;
	payload?: Array<{
		payload: { label: string; paid: number; unpaid: number; total: number };
	}>;
}

const OrdersTooltip: React.FC<OrdersTooltipProps> = ({ active, payload }) => {
	if (!active || !payload || payload.length === 0) return null;
	const p = payload[0].payload;
	return (
		<div className="min-w-[9rem] rounded-lg border bg-popover p-3 text-popover-foreground shadow-md text-xs space-y-1">
			<p className="font-medium">{p.label}</p>
			{tooltipRow("Paid", num(p.paid), "var(--chart-1)")}
			{tooltipRow("Unpaid", num(p.unpaid), "var(--chart-1)", 0.4)}
			{tooltipRow("Total", num(p.total), "transparent")}
		</div>
	);
};

const SubMetric: React.FC<{ label: string; value: string; hint?: string }> = ({
	label,
	value,
	hint,
}) => (
	<div>
		<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
			{label}
		</p>
		<p className="mt-1.5 text-xl font-semibold tracking-tight tabular-nums">
			{value}
		</p>
		{hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
	</div>
);

const MiniStat: React.FC<{ label: string; value: string }> = ({
	label,
	value,
}) => (
	<div>
		<p className="text-xs text-muted-foreground">{label}</p>
		<p className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">
			{value}
		</p>
	</div>
);

const BreakdownCard: React.FC<{
	title: string;
	description: string;
	slices: MerchantVolumeSlice[];
}> = ({ title, description, slices }) => (
	<Card>
		<CardHeader className="pb-2">
			<CardTitle
				role="heading"
				aria-level={2}
				className="text-base font-semibold"
			>
				{title}
			</CardTitle>
			<CardDescription className="text-xs">{description}</CardDescription>
		</CardHeader>
		<CardContent className="space-y-4">
			{slices.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4">No volume yet.</p>
			) : (
				slices.map((s) => {
					const { token, blockchain } = splitTicker(s.label);
					return (
						<div key={s.label} className="space-y-1.5">
							<div className="flex items-center justify-between gap-3">
								<div className="flex items-center gap-2 min-w-0">
									<CryptoIcon
										ticker={s.label}
										showComposite
										className="h-5 w-5 shrink-0"
									/>
									<div className="min-w-0 leading-tight">
										<div className="text-sm font-medium truncate">
											{token || blockchain || s.label}
										</div>
										<div className="text-xs text-muted-foreground truncate">
											{BLOCKCHAIN_NAMES[blockchain] || blockchain}
										</div>
									</div>
								</div>
								<span className="text-sm font-semibold tabular-nums">
									{usd(s.volume)}
								</span>
							</div>
							<div className="h-1.5 rounded-full bg-muted overflow-hidden">
								<div
									className="h-full rounded-full bg-[var(--chart-1)]"
									style={{
										width: `${Math.min(100, Math.max(2, s.percentage))}%`,
									}}
								/>
							</div>
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>{num(s.orderCount)} orders</span>
								<span className="font-mono tabular-nums">
									{s.percentage.toFixed(1)}%
								</span>
							</div>
						</div>
					);
				})
			)}
		</CardContent>
	</Card>
);

const DetailSkeleton: React.FC = () => (
	<div className="space-y-6">
		<Skeleton className="h-9 w-64" />
		<div className="rounded-xl border bg-card p-6 sm:p-8 space-y-6">
			<div className="grid sm:grid-cols-2 gap-8">
				<Skeleton className="h-16 w-full" />
				<Skeleton className="h-16 w-full" />
			</div>
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t">
				{[0, 1, 2, 3].map((i) => (
					<Skeleton key={i} className="h-12 w-full" />
				))}
			</div>
		</div>
		<Skeleton className="h-48 w-full rounded-xl" />
		<Skeleton className="h-64 w-full rounded-xl" />
	</div>
);

export default MerchantDetailPage;
